import React from 'react';
import { Box, CheckboxField, Label, Link, Modal, Paragraph, Set, Stack } from 'bumbag';
import { Field } from 'formik';

import { ApplePayMark, GooglePayMark, MastercardLogo, VisaLogo } from '@medipass/assets/icons';
import { PaymentMethod } from '@medipass/react-core/redux-form';
import { TRANSACTION_PAYMENT_OPTIONS } from '@medipass/utils/constants';
import { isHealthPoint, isHICAPS, isOHC } from '@medipass/utils/funders';
import sdk from '@medipass/web-sdk';

import { ApplePayAvailabilityStatus } from 'components/applePay';
import { canRenderGooglePayOption } from 'components/googlePay';
import RemovePaymentMethodModal from 'legacy/components/checkout/RemovePaymentMethodModal';
import payPalLogo from 'legacy/images/paypal-logo.svg';

type Props = {
  amount?: string;
  applePayStatus?: ApplePayAvailabilityStatus;
  alwaysSavePatientPaymentMethod?: boolean;
  funderCode?: string;
  onChangeCardPaymentMethod: (...args: Array<any>) => any;
  onChangePaymentOption: (...args: Array<any>) => any;
  onUserChangePaymentOption?: (...args: Array<any>) => any;
  onRemovePaymentMethod: (...args: Array<any>) => any;
  cardPaymentMethods?: Array<Record<string, any>>;
  paymentOption?: string | undefined;
  selectedCardPaymentMethod?: Record<string, any>;
  showPayPal?: boolean;
  showExternalPaymentProviders?: boolean;
  showPayInPerson?: boolean;
  showPaymentCard?: boolean;
  showRememberCardDetails?: boolean;
};
PaymentSelection.defaultProps = {
  amount: undefined,
  cardPaymentMethods: [],
  funderCode: undefined,
  paymentOption: undefined,
  selectedCardPaymentMethod: undefined,
  showExternalPaymentProviders: true,
  showPayInPerson: true,
  showPaymentCard: true,
  showRememberCardDetails: false,
  alwaysSavePatientPaymentMethod: false,
};

// @ts-expect-error TS(7031): Binding element 'fn' implicitly has an 'any' type.
function Effect({ fn }) {
  React.useEffect(fn, []); //eslint-disable-line

  return null;
}

function PaymentOption({
  label,
  selected,
  disabled,
  onSelect,
  mark,
  description,
  testId,
}: {
  label: string;
  selected: boolean;
  disabled?: boolean;
  onSelect: () => void;
  mark?: React.ReactNode;
  description?: React.ReactNode;
  testId?: string;
}) {
  return (
    <label
      className={`flex min-h-12 items-center gap-3 border border-solid px-3 py-2 ${
        selected
          ? 'border-primary-lightest bg-info-lightest inset-ring-1 inset-ring-primary-lightest'
          : 'border-transparent border-b-neutral-dark bg-transparent last:border-b-transparent'
      } ${disabled ? 'cursor-default opacity-60' : 'cursor-pointer'}`}
    >
      <input
        type="radio"
        name="payment-option"
        aria-label={label}
        checked={selected}
        disabled={disabled}
        data-testid={testId}
        onChange={onSelect}
      />
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className={`m-0 text-sm leading-5 ${selected ? 'font-semibold' : ''}`}>
          {label === 'Credit card' ? 'Card' : label}
        </span>
        {description && <span className="m-0 text-xs leading-4 text-basic-dark">{description}</span>}
      </span>
      <span aria-hidden="true">{mark}</span>
    </label>
  );
}

export default function PaymentSelection(props: Props) {
  const {
    applePayStatus = 'unsupported',
    alwaysSavePatientPaymentMethod,
    funderCode,
    onChangeCardPaymentMethod,
    onChangePaymentOption,
    onUserChangePaymentOption,
    onRemovePaymentMethod,
    cardPaymentMethods,
    paymentOption,
    selectedCardPaymentMethod,
    showPayPal = false,
    showExternalPaymentProviders,
    showPayInPerson,
    showPaymentCard,
    showRememberCardDetails,
  } = props;
  const removePaymentMethodModal = Modal.useState();
  const [paymentMethodIdToRemove, setPaymentMethodIdToRemove] = React.useState(undefined);
  const shouldShowApplePayOption =
    showExternalPaymentProviders && (applePayStatus === 'checking' || applePayStatus === 'eligible');
  const shouldShowPayInPersonOption =
    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
    showPayInPerson && (isHICAPS(funderCode) || isOHC(funderCode) || isHealthPoint(funderCode));
  const isApplePayChecking = applePayStatus === 'checking';
  const isApplePaySelected = paymentOption === TRANSACTION_PAYMENT_OPTIONS.APPLE_PAY;
  const shouldShowGooglePayOption = showExternalPaymentProviders && canRenderGooglePayOption();
  const paymentOptionCount = [
    shouldShowGooglePayOption,
    shouldShowApplePayOption,
    showPaymentCard,
    shouldShowPayInPersonOption,
    showPayPal,
  ].filter(Boolean).length;

  const handleSelectPaymentOption = (nextPaymentOption: string) => {
    onChangePaymentOption(nextPaymentOption);
    onUserChangePaymentOption?.(nextPaymentOption);
  };

  // @ts-expect-error TS(7006): Parameter 'paymentMethodId' implicitly has an 'any... Remove this comment to see the full error message
  function handleRemovePaymentMethod(paymentMethodId) {
    setPaymentMethodIdToRemove(paymentMethodId);
    removePaymentMethodModal.show();
  }

  async function onClickRemovePaymentMethod() {
    // @ts-expect-error TS(2345): Argument of type 'undefined' is not assignable to ... Remove this comment to see the full error message
    await sdk.selfCheckoutSessions.deletePaymentMethod(paymentMethodIdToRemove);
    await onRemovePaymentMethod(paymentMethodIdToRemove);
    removePaymentMethodModal.hide();
  }

  async function onClickCancelRemovePaymentMethod() {
    setPaymentMethodIdToRemove(undefined);
    removePaymentMethodModal.hide();
  }

  return (
    <Stack spacing="major-2">
      {paymentOptionCount > 1 && (
        <Stack spacing="major-1">
          <Label id="payment-selection-label">How do you want to pay?</Label>
          <div role="radiogroup" aria-labelledby="payment-selection-label" className="flex flex-col">
            {shouldShowGooglePayOption && (
              <>
                <Effect fn={() => onChangePaymentOption(TRANSACTION_PAYMENT_OPTIONS.GOOGLE_PAY)} />
                <PaymentOption
                  label="Google Pay"
                  selected={paymentOption === TRANSACTION_PAYMENT_OPTIONS.GOOGLE_PAY}
                  onSelect={() => handleSelectPaymentOption(TRANSACTION_PAYMENT_OPTIONS.GOOGLE_PAY)}
                  mark={<GooglePayMark height="28px" viewBox="170.7 170 752 400" width="52px" />}
                />
              </>
            )}
            {shouldShowApplePayOption && (
              <PaymentOption
                label="Apple Pay"
                selected={isApplePaySelected}
                disabled={isApplePayChecking}
                testId={isApplePayChecking ? 'apple-pay-option-shell' : undefined}
                onSelect={() => handleSelectPaymentOption(TRANSACTION_PAYMENT_OPTIONS.APPLE_PAY)}
                mark={<ApplePayMark height="28px" width="48px" />}
              />
            )}
            {showPaymentCard && (
              <PaymentOption
                label="Credit card"
                selected={paymentOption === TRANSACTION_PAYMENT_OPTIONS.PAYMENT_CARD}
                onSelect={() => handleSelectPaymentOption(TRANSACTION_PAYMENT_OPTIONS.PAYMENT_CARD)}
                mark={
                  <span className="flex gap-1">
                    <VisaLogo height="24px" width="36px" />
                    <MastercardLogo height="24px" width="30px" />
                  </span>
                }
              />
            )}
            {showPayPal && (
              <PaymentOption
                label="PayPal"
                selected={paymentOption === TRANSACTION_PAYMENT_OPTIONS.PAYPAL}
                onSelect={() => handleSelectPaymentOption(TRANSACTION_PAYMENT_OPTIONS.PAYPAL)}
                description="Pay in full or 4 interest-free payments."
                mark={
                  <img alt="" aria-hidden="true" className="block h-auto" height={18} src={payPalLogo} width={68} />
                }
              />
            )}
            {shouldShowPayInPersonOption && (
              <PaymentOption
                label="In person"
                selected={paymentOption === TRANSACTION_PAYMENT_OPTIONS.PAY_IN_PERSON}
                onSelect={() => handleSelectPaymentOption(TRANSACTION_PAYMENT_OPTIONS.PAY_IN_PERSON)}
              />
            )}
          </div>
        </Stack>
      )}
      {paymentOption === TRANSACTION_PAYMENT_OPTIONS.PAYMENT_CARD && (
        <PaymentMethod
          isRequired
          onChangePaymentMethod={onChangeCardPaymentMethod}
          // @ts-expect-error TS(2769): No overload matches this call.
          paymentMethods={cardPaymentMethods}
          selectedPaymentMethod={selectedCardPaymentMethod}
          showRememberCardDetails={false}
          onClickRemovePaymentMethod={handleRemovePaymentMethod}
          enableRemovePaymentMethod
        />
      )}
      {paymentOption === TRANSACTION_PAYMENT_OPTIONS.PAY_IN_PERSON && (
        <Box fontSize="300" textAlign="center" role="alert">
          Please pay your provider directly for this service.
        </Box>
      )}
      {showRememberCardDetails && (
        <Set>
          <Field
            name="rememberCardDetails"
            component={CheckboxField.Formik}
            checked={alwaysSavePatientPaymentMethod}
            disabled={alwaysSavePatientPaymentMethod}
            checkboxLabel="Save details for next time"
            marginTop="major-2"
          />
        </Set>
      )}
      {showRememberCardDetails && (
        <Paragraph fontSize="12px" lineHeight="16px">
          By saving card details, you authorise this business to charge your card for future purchases you make with
          them. This authorisation will remain in effect until the card expires. Please contact this business for any
          questions relating to how they may use your saved card details.{' '}
          <Link
            href="https://help.tyrohealth.com/en/articles/6426354-manage-your-saved-card-details"
            target="_blank"
            textDecoration="none"
            rel="noreferrer noopener"
          >
            Learn more
          </Link>
        </Paragraph>
      )}
      <RemovePaymentMethodModal
        modal={removePaymentMethodModal}
        onClickCancel={onClickCancelRemovePaymentMethod}
        onClickSubmit={onClickRemovePaymentMethod}
      />
    </Stack>
  );
}
